<template>
  <div class="my-2" data-test-id="student-create-apt-pass-form">
    <Label class="mt-2" required>
      {{ $t("create_pass_student_view.departing_from") }}
    </Label>
    <LazyLoadSelect
      data-test-id="stu-apt-pass-fromid"
      v-model="state.form.from"
      :invalid-feedback="errors.from ? $t(errors.from) : ''"
      :is-multiple="false"
      :selected="state.form.from"
      type="appointment"
      field="departing_from"
      :placeholder="$t('create_pass_student_view.teacher_location_placeholder')"
      @changed="
        (value) => {
          state.form.from = value
        }
      "
    >
    </LazyLoadSelect>
    <InfoBox class="danger mt-2 mb-2" v-if="state.serverErrors.from_id">
      {{ state.serverErrors.from_id[0] }}
    </InfoBox>

    <FavoriteCarousel
      :key="'departing'"
      :selected="state.form.from"
      :is-editable="true"
      :role="currentUser.role.name"
      :current-user="currentUser"
      :action="
        (elem) => {
          state.form.from = elem
        }
      "
      help-center-key="to_add_favorite_teacher_txt_v001"
      :isvalid="errors.from"
      class="mb-3"
      section="departing"
    />

    <Label class="mt-2" required>
      {{ $t("create_pass_student_view.destination") }}
    </Label>

    <LazyLoadSelect
      data-test-id="stu-apt-pass-toid"
      v-model="state.form.to"
      :invalid-feedback="errors.to ? $t(errors.to) : ''"
      :is-multiple="false"
      :selected="state.form.to"
      type="appointment"
      :adult-pass-request-disabled="true"
      :force-clear-cache="true"
      :placeholder="$t('create_pass_student_view.teacher_location_placeholder')"
      @changed="
        (value) => {
          onChangeEvent(value)
        }
      "
    >
      <template #footer> </template>
    </LazyLoadSelect>
    <InfoBox
      v-if="
        state.form.to &&
        state.form.to.value &&
        state.form.to.value.unavailability
      "
      class="danger mt-2 mb-2"
    >
      {{ $t("create_pass_student_view.this_destination_is_unavailable_until") }}
      <span
        class="mx-1"
        v-html="
          $helpers.getHTMLDateTime(
            state.form.to.value.unavailability.to_date,
            'MMMM D, YYYY',
            'h:mm A'
          ) + '.'
        "
      />
      <span v-if="state.form.to.value.unavailability.comment"
        >{{ $t("common.note") }}:
        {{ state.form.to.value.unavailability.comment }}</span
      >
    </InfoBox>
    <InfoBox class="danger mt-2 mb-2" v-if="state.serverErrors.to_id">
      {{ state.serverErrors.to_id[0] }}
    </InfoBox>

    <FavoriteCarousel
      :key="'destination'"
      :selected="state.form.to"
      :is-editable="true"
      :show-unavailables="true"
      :role="currentUser.role.name"
      :current-user="currentUser"
      :action="
        (elem) => {
          onChangeEvent(elem)
        }
      "
      :isvalid="errors.to"
      section="destination"
    />

    <div class="apt-pass-datetime-row row mt-3 g-3 align-items-start">
      <div class="col-12 col-md-4 apt-pass-datetime-col">
        <Label required :html-for="'stu-apt-pass-date-input'">{{
          $t("common.date")
        }}</Label>
        <div data-test-id="stu-apt-pass-date" class="w-100">
          <CustomDatePicker
            v-model="state.form.for_date"
            :masks="{ L: 'MM/DD/YYYY' }"
            input-class="d-block"
            input-id="stu-apt-pass-date-input"
            :placeholder="$t('common.date')"
            :min-date="minDate"
            :max-date="maxDate"
            @update:model-value="checkUnavailability"
            :invalid-feedback="errors.for_date ? $t(errors.for_date) : ''"
          >
          </CustomDatePicker>
        </div>
      </div>
      <div class="col-12 col-md-4 apt-pass-datetime-col">
        <Label required :html-for="'stu-apt-pass-period-input'">{{
          $t("common.period")
        }}</Label>
        <CustomSelect
          data-test-id="stu-apt-pass-period"
          v-if="periods"
          v-model="state.form.period"
          input-id="stu-apt-pass-period-input"
          :options="periods"
          :placeholder="$t('common.select_one')"
          :hide-floating-label="true"
          :accessible-label="$t('common.period')"
          :clearable="false"
          :filterable="true"
          :close-on-select="true"
          :invalid-feedback="errors.period ? $t(errors.period) : ''"
        ></CustomSelect>
      </div>
      <div class="col-12 col-md-4 apt-pass-datetime-col">
        <Label required :html-for="'stu-apt-pass-time-input'">{{
          $t("common.time")
        }}</Label>
        <CustomTimePicker
          data-test-id="stu-apt-pass-time"
          format="HH:mm"
          input-id="stu-apt-pass-time-input"
          :accessible-label="$t('common.time')"
          v-model="state.form.time"
          :placeholder="$t('common.time')"
          @update:model-value="checkUnavailability"
          @input="checkUnavailability"
          :invalid-feedback="errors.time ? $t(errors.time) : ''"
        >
        </CustomTimePicker>
        <div v-if="state.serverErrors.time" class="invalid-message text-start">
          {{ state.serverErrors.time[0] }}
        </div>
      </div>
    </div>

    <div
      v-if="
        state.form &&
        state.form.to &&
        state.form.to.value &&
        state.form.to.value.comment_type != 'Hidden'
      "
      class="mt-2"
    >
      <Label label-id="apt-pass-reason-lbl" html-for="apt-pass-reason">
        {{ $t("common.reason") }}
      </Label>
      <InputField
        id="apt-pass-reason"
        label-id="apt-pass-reason-lbl"
        data-test-id="stu-apt-pass-comment"
        v-model="state.form.reason"
        :invalid-feedback="errors.reason ? $t(errors.reason) : ''"
        max-chars="250"
        :rows="5"
        class="w-full"
        type="textarea"
        :placeholder="
          state.form.to.value.comment_type == 'Optional'
            ? $t('create_pass_student_view.reason_for_pass_optional')
            : $t('create_pass_student_view.reason_for_pass_required')
        "
      />
      <div v-if="state.serverErrors.reason" class="invalid-message text-start">
        {{ state.serverErrors.reason[0] }}
      </div>
    </div>

    <InfoBox
      v-if="state.serverRes"
      :key="`${state.serverRes.title}-${state.serverRes.message}`"
      data-test-id="stu-apt-pass-form-error"
      class="mt-3"
      announce-assertive
      :class="{
        danger:
          Object.keys(state.serverErrors).length ||
          state.serverRes.title == 'Error'
      }"
      :title="state.serverRes.title"
      >{{ state.serverRes.message }}</InfoBox
    >

    <div class="form-group form-actions mt-2 text-center">
      <div class="d-inline">
        <LoadingButton
          :is-loading="state.isLoading"
          :disabled="cooldownState.isCooldownActive"
          data-test-id="create-apt-pass-form-submit"
          solid
          rounded
          class="mt-2 btn"
          @click="submit()"
        >
          {{ $t("common.submit") }}
        </LoadingButton>
      </div>
    </div>
  </div>
</template>

<script>
import useCooldown from "@/composables/useCooldown.js"
import helpersJS from "@/helpers/index.js"
import FavoriteCarousel from "@/v3components/FavoriteCarousel.vue"
import AptPassSuccesModal from "@/v3components/Forms/Students/AptPassSuccesModal.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import CustomDatePicker from "@/v3components/shared/Form/CustomDatePicker.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomTimePicker from "@/v3components/shared/Form/CustomTimePicker.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import useVuelidate from "@vuelidate/core"
import { helpers, required, requiredIf } from "@vuelidate/validators"
import moment from "moment-timezone"
import { computed, inject, reactive, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "CreateAppointmentPassForm",
  components: {
    LazyLoadSelect,
    FavoriteCarousel,
    Label,
    CustomDatePicker,
    CustomTimePicker,
    CustomSelect,
    InputField,
    InfoBox,
    LoadingButton
  },
  setup() {
    const store = useStore()
    const { t } = useI18n()

    const modal = inject("modal")

    const { state: cooldownState, startCooldown } = useCooldown()

    const state = reactive({
      isLoading: false,
      form: {
        from: [],
        to: [],
        reason: "",
        for_date: null,
        time: null
      },
      serverErrors: {},
      serverRes: null,
      requestIntreval: null,
      showAppPassSuccesModal: false
    })
    const currentUser = computed(() => store.getters["authentication/user"])

    const periods = computed(() => store.getters["passes/formListPeriods"])
    const minDate = computed(() => {
      return moment().format("MMM DD YYYY")
    })
    const maxDate = computed(() => {
      const oneMonthFromNow = moment().add(1, "M").format("MMM DD YYYY")
      const maxSchoolYearDate = helpersJS.maxSchoolYearDate()
      return moment(oneMonthFromNow).isBefore(moment(maxSchoolYearDate))
        ? oneMonthFromNow
        : maxSchoolYearDate
    })

    const validationMessages = {
      required: "common.this_field_is_required",
      requiredReason: "common.reason_for_pass_is_required",
      minTime:
        "common.the_time_must_be_30_minutes_from_now_and_not_more_than_a_month",
      betweenTime: "common.you_must_pick_a_time_between_6am_and_5pm"
    }

    const validations = computed(() => ({
      form: {
        from: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        to: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        reason: {
          required: helpers.withMessage(
            validationMessages.requiredReason,
            requiredIf(() => {
              if (state.form.to && state.form.to.value) {
                return state.form.to.value.comment_type == "Optional"
                  ? false
                  : state.form.to.value.comment_type == "Hidden"
                    ? false
                    : true
              }
            })
          )
        },
        time: {
          betweenTime: helpers.withMessage(
            validationMessages.betweenTime,
            () => {
              return moment(state.form.time, "HH:mm a").isBetween(
                moment("05:59 AM", "HH:mm a"),
                moment("05:01 PM", "HH:mm a")
              )
            }
          ),
          minTime: helpers.withMessage(validationMessages.minTime, () => {
            if (
              state.form.for_date &&
              state.form.time &&
              moment()
                .add(30, "minutes")
                .isSameOrBefore(
                  moment(
                    moment(
                      new Date(state.form.for_date).toLocaleDateString("en-GB"),
                      "DD-MM-YYYY"
                    ).format("YYYY-MM-DD") +
                      " " +
                      state.form.time
                  )
                )
            ) {
              return true
            } else {
              return false
            }
          }),

          required: helpers.withMessage(validationMessages.required, required)
        },
        for_date: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        period: {
          required: helpers.withMessage(validationMessages.required, required)
        }
      }
    }))

    const v$ = useVuelidate(validations.value.form, state.form)

    const isValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    const onChangeEvent = (value) => {
      state.form.to = value
      v$.value.$reset()
      setResponseInfoBox()
      checkUnavailability()
    }

    const checkUnavailability = () => {
      if (!state.form.to?.value || !state.form.for_date || !state.form.time) {
        state.form.to?.value && (state.form.to.value.unavailability = null)
        return
      }
      clearTimeout(state.requestIntreval)
      state.requestIntreval = setTimeout(() => {
        const params = {
          type: state.form.to.value.type,
          id: state.form.to.value.id,
          apt_for_time: helpersJS.currTzDateTime(
            state.form.for_date,
            state.form.time
          )
        }
        store
          .dispatch("rooms/getUnavailability", params)
          .then((response) => {
            if (response.data.data) {
              state.form.to.value.unavailability = response.data.data
            } else {
              state.form.to.value.unavailability = null
            }
          })
          .catch(() => {
            state.form.to.value.unavailability = null
          })
      }, 600)
    }

    const submit = () => {
      if (cooldownState.isCooldownActive) {
        return
      }
      setResponseInfoBox()
      state.isLoading = true
      const isAptToRoom =
        state.form?.to?.value?.type == "App\\Models\\Room" &&
        v$.value.$silentErrors?.length == 1

      if (isValid.value || isAptToRoom) {
        const appointmentPass = {
          from_id: state.form.from.value.id,
          to_id: state.form.to.value.id,
          from_type: state.form.from.value.type,
          to_type: state.form.to.value.type,
          for_date: helpersJS.currTzDateTime(
            state.form.for_date,
            state.form.time
          ),
          time: helpersJS.currTzDateTime(state.form.for_date, state.form.time),
          reason: state.form.reason,
          period_id: state.form.period.value[0]
        }

        store
          .dispatch(
            "studentAptPass/createStudentAppointmentPass",
            appointmentPass
          )
          .then(() => {
            state.isLoading = true
            state.serverErrors = {}
            setResponseInfoBox(
              "Success",
              t(
                "create_pass_student_view.appointment_pass_created_successfully"
              )
            )

            modal.open(AptPassSuccesModal, {
              size: "lg",
              title: "",
              props: { form: state.form }
            })
          })
          .catch((err) => {
            const res = err.response.data
            state.serverErrors = res.errors
              ? res.errors
              : { errorSubmit: ["Submit failed. Please try again later."] }
            setResponseInfoBox("Error", res.message)
            state.isLoading = false
            startCooldown() // Start 30-second cooldown
          })
      } else {
        v$.value.$touch()
        state.isLoading = false
      }
    }

    const closePassSuccess = () => {
      setResponseInfoBox()
      state.form = {
        from: [],
        to: [],
        reason: "",
        for_date: null,
        time: null
      }
      v$.value.$reset()
      state.isLoading = false
    }

    watch(
      () => modal.options.action,
      (isAction) => {
        if (isAction == "closeStudentAptPassSuccessModal") {
          closePassSuccess()
        }
      }
    )

    watch(
      () => modal.options.visible,
      (isVisible) => {
        if (!isVisible) {
          closePassSuccess()
        }
      }
    )

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    return {
      state,
      cooldownState,
      periods,
      minDate,
      onChangeEvent,
      submit,
      checkUnavailability,
      closePassSuccess,
      errors,
      setResponseInfoBox,
      maxDate,
      currentUser
    }
  }
}
</script>

<style scoped lang="scss">
.periods {
  #vs5__listbox {
    max-height: 110px !important;
  }
}
.ap-cs-date-picker .input-icon-field .form-control.is-invalid {
  pointer-events: auto;
  // margin-bottom: 7rem !important;
}

/* Equal-width columns; flex items default min-width:auto was widening the time column */
.apt-pass-datetime-row {
  .apt-pass-datetime-col {
    min-width: 0;
  }
  :deep(.v3-custom-select) {
    width: 100%;
  }
}
</style>
